import React from 'react'
import { t } from 'ttag'
import { Accordion } from 'components/shared/Accordion/Accordion'
import { ScoreChip } from 'components/shared/ScoreChip/ScoreChip'
import cx from 'classnames'
import { Button } from 'components/shared/Button/Button'
import { HideExtensiveText } from 'components/shared/HideExtensiveText/HideExtensiveText'
import { percentScore } from 'utils/stringFormat'
import { getInterviewScoreTreshholds } from 'utils/score'
import { useInternalState } from './hooks'
import { Box } from 'components/layout/Box/Box'
import type { InterviewEvaluator, InterviewSection } from '../../../../types'

import styles from './Reviews.module.scss'

type SkillReview = {
  comment?: string | null
  comments?: Record<string, string | null | undefined> | null
  score?: number | null
}

type Skill = {
  reviews?: Record<string, SkillReview> | null
  score?: number | null
  title?: string
}

type ReviewsProps = {
  evaluators?: InterviewEvaluator[]
  sections?: InterviewSection[]
  skills: Record<string, unknown>
}

const getQuestionTitle = (
  sections: InterviewSection[] | undefined,
  sectionKey,
  questionId,
) => {
  const section = (sections ?? []).find(section => section?.key === sectionKey)
  const question = (section?.questions ?? []).find(
    q => String(q.id) === String(questionId),
  )
  return question?.title ?? null
}

export const Reviews = ({
  evaluators,
  sections = [],
  skills,
}: ReviewsProps) => {
  const skillsArray = Object.entries(skills ?? {}) as [string, Skill][]

  const {
    state: { allReviewsOpen, reviewsOpenState },
    handlers: { toggleAllReviews, toggleReview },
  } = useInternalState({ skillsArray: skillsArray.map(([key]) => key) })

  const getReviews = (selectedSkills: [string, Skill][]) =>
    selectedSkills.map(([key, { reviews, score, title }]) => (
      <Accordion
        key={key}
        className={styles.accordion}
        isInitiallyOpen={reviewsOpenState[key]}
      >
        <Accordion.Header
          className={styles.scoresHeader}
          onToggle={() => toggleReview(key)}
          title={
            <span className={styles.scoresTitle}>
              <span>{title}</span>
              <ScoreChip
                score={percentScore(score)}
                theme={getInterviewScoreTreshholds(score)}
                size="medium"
              />
            </span>
          }
          buttonClassName={styles.button}
        />
        <Accordion.Panel className={styles.panel}>
          <div className={styles.responses}>
            {Object.entries(reviews ?? {}).map(
              ([email, { comment, comments = {}, score }]) => {
                const name = evaluators!.find(
                  evaluator => email === evaluator.email,
                )?.name
                const questionComments = Object.entries(comments ?? {}).filter(
                  ([, text]) => text && String(text).trim(),
                ) as [string, string][]
                const hasQuestionNotes = questionComments.length > 0
                const hasLegacyComment = Boolean(comment)

                return (
                  <div key={email} className={styles.response}>
                    {name ? (
                      <span className={styles.responseName}>{name}</span>
                    ) : null}
                    <span className={styles.responseScore}>
                      <ScoreChip
                        score={percentScore(score)}
                        className={styles.responseChip}
                        size="small"
                      />
                    </span>
                    <div className={styles.responseComments}>
                      {hasQuestionNotes ? (
                        questionComments.map(([questionId, noteText]) => {
                          const questionTitle = getQuestionTitle(
                            sections,
                            key,
                            questionId,
                          )
                          return (
                            <div
                              key={questionId}
                              className={styles.responseCommentBlock}
                            >
                              {questionTitle ? (
                                <span className={styles.responseCommentLabel}>
                                  {questionTitle}
                                </span>
                              ) : null}
                              <span className={styles.responseComment}>
                                <HideExtensiveText text={noteText} />
                              </span>
                            </div>
                          )
                        })
                      ) : hasLegacyComment ? (
                        <span className={styles.responseComment}>
                          <HideExtensiveText text={comment!} />
                        </span>
                      ) : (
                        <span
                          className={cx(styles.responseComment, {
                            [styles.noComment]: true,
                          })}
                        >
                          {t`no comment`}
                        </span>
                      )}
                    </div>
                  </div>
                )
              },
            )}
          </div>
        </Accordion.Panel>
      </Accordion>
    ))

  return (
    <Box direction="column" className={styles.reviews}>
      <Button
        label={allReviewsOpen ? t`Hide all` : t`Show all`}
        size="small"
        variant="text"
        className={styles.showButton}
        onClick={toggleAllReviews}
      />
      <Box className={styles.scores} justify="space-between">
        <Box direction="column" className={styles.column}>
          {getReviews(skillsArray.filter((_, index) => index % 2 === 0))}
        </Box>
        <Box direction="column" className={styles.column}>
          {getReviews(skillsArray.filter((_, index) => index % 2 === 1))}
        </Box>
      </Box>
    </Box>
  )
}
